import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type { Person } from '../../types';
import type {
  CandidateLanguageEntry,
  LanguageInput,
} from '../../types/candidateDetails';
import { LANGUAGE_PROFICIENCIES } from '../../types/candidateDetails';
// @ts-ignore
import { peopleApi } from '../../services/api';

/**
 * Mid-sized modal editor for `person_languages` (full-wipe PUT, same model
 * as domains). Wireframe: docs/designs/candidate-edit-flows.svg section H.
 */

import { adminProfileApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';

export interface LanguagesEditorModalProps {
  person: Person;
  apiClient?: ProfileMutationsApi;
  onClose: () => void;
  onSaved: (updated: Person) => void;
}

interface RowForm {
  localId: string;
  language: string;
  proficiency: string;
}

let _localIdCounter = 0;
function nextLocalId(): string {
  _localIdCounter += 1;
  return `lang-${_localIdCounter}-${Date.now().toString(36)}`;
}

function rowFromEntry(e: CandidateLanguageEntry): RowForm {
  return {
    localId: nextLocalId(),
    language: e.language ?? '',
    proficiency: e.proficiency ?? '',
  };
}

function blankRow(): RowForm {
  return { localId: nextLocalId(), language: '', proficiency: '' };
}

function rowToPayload(r: RowForm): LanguageInput {
  return {
    language: r.language.trim(),
    proficiency: r.proficiency.trim() === '' ? null : r.proficiency.trim(),
  };
}

interface ParsedError {
  index: number | null;
  leaf: string;
}

function parseErrorField(field: string | undefined): ParsedError | null {
  if (!field) return null;
  const m = /^languages\[(\d+)\]\.?(.*)$/.exec(field);
  if (!m) return { index: null, leaf: field };
  return { index: Number(m[1]), leaf: m[2] ?? '' };
}

export const LanguagesEditorModal: React.FC<LanguagesEditorModalProps> = ({
  person,
  apiClient = adminProfileApi,
  onClose,
  onSaved,
}) => {
  const initialRows = useMemo<RowForm[]>(() => {
    const src = person.CandidateDetails?.languages ?? [];
    if (src.length === 0) return [];
    return src.map(rowFromEntry);
  }, [person]);

  const [rows, setRows] = useState<RowForm[]>(initialRows);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelTrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelTrapRef, true);

  const dirty = useMemo(() => {
    return JSON.stringify(initialRows) !== JSON.stringify(rows);
  }, [initialRows, rows]);

  const requestClose = useCallback(() => {
    if (dirty) {
      const ok = window.confirm('Discard unsaved language changes?');
      if (!ok) return;
    }
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        requestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current != null) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  const rowHasError = (i: number, leaf: string): boolean => {
    if (!fieldError?.field) return false;
    const p = parseErrorField(fieldError.field);
    if (p == null || p.index === null) return false;
    if (p.index !== i) return false;
    return p.leaf === leaf || p.leaf === '';
  };

  const handleSave = async () => {
    if (!person.id) return;
    setSaving(true);
    setServerError(null);
    setFieldError(null);
    const payload: LanguageInput[] = rows.map(rowToPayload);
    try {
      const updated = await apiClient.replaceLanguages(person.id, payload);
      onSaved(updated);
    } catch (e: unknown) {
      const ax = e as {
        response?: { status?: number; data?: { error?: string; field?: string; limit?: number } };
      };
      const status = ax.response?.status;
      const data = ax.response?.data;
      const msg = data?.error || (e instanceof Error ? e.message : 'Save failed');
      setFieldError(
        data?.field && data?.error ? { field: data.field, message: data.error } : null
      );
      if (status === 413 && data?.field === 'languages') {
        setServerError(
          data.limit != null
            ? `${msg} (limit ${data.limit})`
            : msg
        );
      } else if (!data?.field) {
        setServerError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const scrollToRow = (index: number) => {
    if (scrollTimerRef.current != null) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      const el = document.querySelector(`[data-lang-row="${index}"]`);
      if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
        (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 0);
  };

  useEffect(() => {
    if (!fieldError?.field) return;
    const p = parseErrorField(fieldError.field);
    if (p?.index != null && p.index >= 0) scrollToRow(p.index);
  }, [fieldError]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4"
      data-testid="languages-editor-modal"
      role="presentation"
    >
      <div
        ref={panelTrapRef}
        className="relative w-full max-w-xl max-h-[85vh] overflow-hidden rounded-xl border border-dark-border bg-dark-bg shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="languages-editor-title"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-dark-border px-5 py-4 shrink-0">
          <div>
            <h2 id="languages-editor-title" className="text-lg font-semibold text-dark-text">
              Edit languages
            </h2>
            <p className="text-xs text-dark-text-secondary mt-1">
              Replaces all saved languages for this candidate (same as domains). Parser-only rows
              in other tables are unchanged.
            </p>
          </div>
          <button
            type="button"
            className="text-dark-text-secondary hover:text-dark-text text-xl leading-none px-2"
            aria-label="Close"
            onClick={requestClose}
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 flex-1 space-y-3">
          {serverError && (
            <div
              className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-200"
              data-testid="languages-editor-server-error"
            >
              {serverError}
            </div>
          )}

          {rows.length === 0 && (
            <p className="text-sm text-dark-text-secondary" data-testid="languages-editor-empty-hint">
              No rows yet — add a language or save to clear all stored languages.
            </p>
          )}

          {rows.map((row, i) => (
            <div
              key={row.localId}
              data-lang-row={i}
              className={`grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2 items-end rounded-lg border p-3 ${
                rowHasError(i, 'language') || rowHasError(i, 'proficiency')
                  ? 'border-red-600 bg-red-950/20'
                  : 'border-dark-border bg-dark-bg/40'
              }`}
            >
              <div>
                <label className="block text-xs text-dark-text-secondary mb-1">Language</label>
                <input
                  className="w-full rounded-md border border-dark-border bg-dark-bg px-2 py-1.5 text-sm text-dark-text"
                  value={row.language}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, language: v } : r)));
                  }}
                  placeholder="e.g. English"
                  maxLength={80}
                  aria-invalid={rowHasError(i, 'language')}
                />
              </div>
              <div>
                <label className="block text-xs text-dark-text-secondary mb-1">Proficiency</label>
                <select
                  className="w-full rounded-md border border-dark-border bg-dark-bg px-2 py-1.5 text-sm text-dark-text"
                  value={row.proficiency}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, proficiency: v } : r)));
                  }}
                  aria-invalid={rowHasError(i, 'proficiency')}
                >
                  <option value="">(none)</option>
                  {LANGUAGE_PROFICIENCIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="text-sm text-red-300 hover:text-red-200 justify-self-end sm:justify-self-center pb-1"
                onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            className="w-full rounded-lg border border-dashed border-dark-border py-2 text-sm text-dark-text-secondary hover:bg-dark-bg/60"
            data-testid="languages-editor-add-row"
            onClick={() => setRows((prev) => [...prev, blankRow()])}
          >
            + Add language
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-dark-border px-5 py-3 shrink-0">
          <button
            type="button"
            className="rounded-lg border border-dark-border px-4 py-2 text-sm text-dark-text-secondary hover:bg-dark-bg/60"
            onClick={requestClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            data-testid="languages-editor-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
